import { Alert, Box, Button, Divider, Grid, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { ArticleAccount } from 'src/shared/models/invoiceGroup/articleAccount';
import { FeeArticleRow, FeeArticleSettings } from 'src/shared/models/invoiceGroup/feeArticle';
import { getArticleAccountsByOwnerId } from 'src/shared/services/articleAccountService';
import { getFeeArticlesByOwnerId, saveFeeArticle } from 'src/shared/services/feeArticleService';

// Nyckeln som skiljer ett val från ett annat. En nivå eller ett profilvärde, aldrig båda.
const feeKey = (row: FeeArticleRow) => (row.level ? `level:${row.level}` : `profile:${row.memberProfileItem_id}`);

export const AdminInvoiceGroupFeeArticlesTab = () => {
  const params = useParams();
  const { t } = useTranslation(['invoice', 'common']);
  const isMounted = useIsMounted();
  const ownerId = params.id ? Number(params.id) : 0;

  const [articles, setArticles] = useState<ArticleAccount[]>([]);
  const [settings, setSettings] = useState<FeeArticleSettings | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    if (ownerId <= 0) return;
    setLoading(true);
    try {
      const [articleResult, feeResult] = await Promise.all([getArticleAccountsByOwnerId(ownerId), getFeeArticlesByOwnerId(ownerId)]);
      if (!isMounted()) return;

      setArticles(articleResult.data);
      setSettings(feeResult.data);
      setValues(
        [...feeResult.data.levels, ...feeResult.data.profiles].reduce((acc: Record<string, string>, item) => {
          acc[feeKey(item)] = item.articleNr ?? '';
          return acc;
        }, {}),
      );
    } catch (err) {
      if (isMounted()) ConsoleHelper.log(err);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [ownerId, isMounted]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const allRows = useMemo(() => [...(settings?.levels ?? []), ...(settings?.profiles ?? [])], [settings]);

  const changed = useMemo(() => allRows.filter((item) => (values[feeKey(item)] ?? '') !== (item.articleNr ?? '')), [allRows, values]);

  const onSave = async () => {
    setSaving(true);
    try {
      for (let i = 0; i < changed.length; i += 1) {
        const item = changed[i];
        // eslint-disable-next-line no-await-in-loop
        await saveFeeArticle({
          owner_id: ownerId,
          level: item.level,
          memberProfileItem_id: item.memberProfileItem_id,
          articleNr: values[feeKey(item)] ?? '',
        });
      }

      if (isMounted()) {
        toast.success(<>{t('invoice:adminInvoiceGroupFeeArticlesTab.saveSuccess')}</>);
        fetchAll();
      }
    } catch (err) {
      ConsoleHelper.log(err);
      toast.error(<>{t('invoice:adminInvoiceGroupFeeArticlesTab.saveError')}</>);
    } finally {
      if (isMounted()) setSaving(false);
    }
  };

  const renderRow = (row: FeeArticleRow, disabled: boolean) => {
    const key = feeKey(row);

    return (
      <TableRow key={key}>
        <TableCell sx={{ verticalAlign: 'top', pt: 3 }}>
          <Typography variant='body2'>{row.label}</Typography>
        </TableCell>
        <TableCell sx={{ width: 320 }}>
          <TextField
            select
            size='small'
            fullWidth
            disabled={disabled}
            value={values[key] ?? ''}
            onChange={(event) => setValues((prev) => ({ ...prev, [key]: event.target.value }))}
          >
            <MenuItem value=''>
              <em>{t('invoice:adminInvoiceGroupFeeArticlesTab.defaultArticle')}</em>
            </MenuItem>
            {articles.map((article) => (
              <MenuItem key={article.articleNr} value={article.articleNr}>
                {article.articleNr} - {article.name}
              </MenuItem>
            ))}
          </TextField>
        </TableCell>
      </TableRow>
    );
  };

  const renderSection = (
    rows: FeeArticleRow[],
    titleKey: string,
    descriptionKey: string,
    headingKey: string,
    lockedMessage?: string,
    disabled = false,
  ) => (
    <Grid container spacing={2} mb={4}>
      <Grid item xs={12} lg={8}>
        <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
          {t(titleKey)}
        </Typography>
        <Typography variant='body2' color={(theme) => theme.palette.text.secondary} mb={1}>
          {t(descriptionKey)}
        </Typography>
        {lockedMessage && (
          <Alert severity='info' sx={{ mb: 1 }}>
            {lockedMessage}
          </Alert>
        )}
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>{t(headingKey)}</TableCell>
              <TableCell>{t('invoice:adminInvoiceGroupFeeArticlesTab.article')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{rows.map((row) => renderRow(row, disabled))}</TableBody>
        </Table>
      </Grid>
    </Grid>
  );

  if (loading) return null;

  const levels = settings?.levels ?? [];
  const profiles = settings?.profiles ?? [];
  const profilesLocked = settings?.allAssociationsDistributed ?? false;

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} lg={8}>
          <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
            {t('invoice:adminInvoiceGroupFeeArticlesTab.title')}
          </Typography>
          <Typography variant='body2' color={(theme) => theme.palette.text.secondary}>
            {t('invoice:adminInvoiceGroupFeeArticlesTab.description')}
          </Typography>
          <Alert severity='info' sx={{ mt: 1 }}>
            {t('invoice:adminInvoiceGroupFeeArticlesTab.notice')}
          </Alert>
        </Grid>
      </Grid>

      {levels.length > 0 &&
        renderSection(
          levels,
          'invoice:adminInvoiceGroupFeeArticlesTab.distributedTitle',
          'invoice:adminInvoiceGroupFeeArticlesTab.distributedDescription',
          'invoice:adminInvoiceGroupFeeArticlesTab.feePart',
        )}

      {profiles.length > 0 &&
        renderSection(
          profiles,
          'invoice:adminInvoiceGroupFeeArticlesTab.profileTitle',
          'invoice:adminInvoiceGroupFeeArticlesTab.profileDescription',
          'invoice:adminInvoiceGroupFeeArticlesTab.profile',
          // Fördelningen avgörs per förening. Fältet låses bara när den gäller samtliga, för
          // annars finns det medlemmar som valet faktiskt styr.
          profilesLocked
            ? (t('invoice:adminInvoiceGroupFeeArticlesTab.profileLocked') as string)
            : (t('invoice:adminInvoiceGroupFeeArticlesTab.profileAppliesTo') as string),
          profilesLocked,
        )}

      {levels.length === 0 && profiles.length === 0 && <Alert severity='info'>{t('invoice:adminInvoiceGroupFeeArticlesTab.nothingToChoose')}</Alert>}

      <Divider />
      <Grid display='flex' justifyContent='flex-end' mt={2}>
        <Button type='button' variant='contained' disableElevation disabled={changed.length === 0 || saving} onClick={onSave}>
          {t('common:button.save')}
        </Button>
      </Grid>
    </Box>
  );
};
